import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { CalendarEvent, useCalendarEventCalendarDetailQuery } from "../../../../Utilities";
import { CalendarEventLink } from "../../../Links";
import { CalendarEventDetailDisplay, DetailDisplayCalendarEvent } from "../DetailDisplay";
import { ControlledIndividualStatisticsTile } from "../Statistics";
import styles from "./styles.module.css";

export type ControlledCalendarEventAdminModalProps = Omit<
	CalendarEventAdminModalProps,
	"isLoading" | "calendarEvent"
> & {
	calendarEventID: CalendarEvent["id"];
};

export function ControlledCalendarEventAdminModal({
	calendarEventID,
	...props
}: ControlledCalendarEventAdminModalProps) {
	const { loading, data } = useCalendarEventCalendarDetailQuery({ variables: { id: calendarEventID } });
	const calendarEvent = data?.calendarEventConnection?.edges[0]?.node;

	return <CalendarEventAdminModal isLoading={loading} calendarEvent={calendarEvent} {...props} />;
}

export type AdminModalCalendarEvent = DetailDisplayCalendarEvent;

export type CalendarEventAdminModalProps = {
	isLoading?: boolean;
	calendarEvent: AdminModalCalendarEvent | null | undefined;
	withBackButton?: boolean;
} & Pick<ModalProps, "ifRef" | "onClose"> &
	Partial<ClassNameProps & StyleProps>;

export function CalendarEventAdminModal({
	isLoading = false,
	calendarEvent,
	withBackButton = false,
	ifRef,
	onClose,
	className,
	style,
}: CalendarEventAdminModalProps) {
	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["calendar-event-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ marginRight: "1.5rem" }}>
					<If condition={withBackButton}>
						{onClose && (
							<Button variant="link" size="small" onClick={onClose}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
									<Icon.ArrowLeft size={14} /> Back to Calendar
								</Row>
							</Button>
						)}
					</If>

					<Row justify="space-between">
						<Heading.H2 noMargin>Event Details</Heading.H2>
					</Row>
				</Column>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={!calendarEvent && isLoading}>Loading...</If>
					<Else>
						{!!calendarEvent && <CalendarEventDetailDisplay calendarEvent={calendarEvent} withStatus editable />}
					</Else>
				</Conditional>
			</Modal.Body>
			{!!calendarEvent && (
				<Modal.Footer>
					<Row justify="space-between" align="center">
						<ControlledIndividualStatisticsTile calendarEventID={calendarEvent.id} />

						<CalendarEventLink
							instance={calendarEvent}
							className={makeClassName(styles["detail-link"], "link--no-text-decoration")}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								<span>View Page</span>
								<Icon.ArrowRight size={14} />
							</Row>
						</CalendarEventLink>
					</Row>
				</Modal.Footer>
			)}
		</Modal>
	);
}
